import { calculateEstimatedSupplySize } from '@/features/subscription/utils/subscription-cart.util';
import { CouponFlat, CouponTypeEnum } from '@/models/coupon.model.ts';
import {
  MedicationInstructionsFrequency,
  MedicationInstructionsQuantity
} from '@/models/medication-instructions.model';
import { DoseUnitEnum, Medication } from '@/models/medication.model';
import { ProductFlat } from '@/models/product.model';
import { ShippingMethod } from '@/models/shipping-method.model';
import { Interval } from '@/types/common.type';
import { convertIntervalToMonths } from '@/utils/time.util';

export const getProductQuantityText = (
  product: ProductFlat,
  doseUnit: DoseUnitEnum
): string => {
  return (
    product.quantityDisplay ||
    `${product.doseTotalValue} ${doseUnit}${product.doseTotalValue > 1 ? 's' : ''}`
  );
};

export const getInstructionsDisplayText = (
  instructionsQuantity: MedicationInstructionsQuantity | null,
  instructionsFrequency: MedicationInstructionsFrequency | null
): string | null => {
  return instructionsQuantity && instructionsFrequency
    ? instructionsQuantity.text +
        ' ' +
        (instructionsFrequency.isPrn
          ? 'PRN'
          : instructionsFrequency.text.toLowerCase())
    : null;
};

export const getDefaultShippingMethod = (
  medications: Medication[]
): ShippingMethod | null => {
  return medications.reduce<ShippingMethod | null>((res, medication) => {
    const shippingMethod = medication.defaultShippingMethod;

    if (!shippingMethod) return res;

    if (!res || res.speedScore < shippingMethod.speedScore)
      return shippingMethod;

    return res;
  }, null);
};

export const getAllAvailableProductsForMedication = (props: {
  medication: Medication;
  billingInterval: Interval;
}): ProductFlat[] => {
  const { medication, billingInterval } = props;

  let res = [...(medication?.products || [])];

  if (medication?.isQtyMatchDuration) {
    const intervalMonths = convertIntervalToMonths(billingInterval);
    res = res.filter((product) => product.volume === intervalMonths);
  }

  res.sort((a, b) => a.doseTotalValue - b.doseTotalValue);

  return res;
};

export const findProductForInstructionsAndInterval = (props: {
  medication: Medication;
  interval: Interval;
  instructionsQuantity: MedicationInstructionsQuantity | null;
  instructionsFrequency: MedicationInstructionsFrequency | null;
}): ProductFlat | null => {
  const { medication, interval, instructionsQuantity, instructionsFrequency } =
    props;

  if (!instructionsQuantity || !instructionsFrequency) return null;

  const allProducts = getAllAvailableProductsForMedication({
    medication,
    billingInterval: interval
  });

  const products = [...allProducts];
  products.sort((a, b) => a.doseTotalValue - b.doseTotalValue);

  const estimatedQuantity = calculateEstimatedSupplySize({
    interval,
    instructionsQuantity,
    instructionsFrequency
  });

  const upperBoundProducts = products.filter(
    (item) => item.doseTotalValue >= estimatedQuantity
  );

  return upperBoundProducts[0] || null;
};

export const calculateDiscountAmount = (
  coupon: CouponFlat,
  carePlanAmount: number,
  subtotal: number,
  shippingAmount: number
): number => {
  let discountAmount = 0;
  switch (coupon.couponType) {
    case CouponTypeEnum.AMOUNT_OFF_CARE_PLAN:
      discountAmount = Math.min(coupon.amountOff ?? 0, carePlanAmount);
      break;

    case CouponTypeEnum.AMOUNT_OFF_INVOICE:
      discountAmount = Math.min(coupon.amountOff ?? 0, subtotal);
      break;

    case CouponTypeEnum.PERCENTAGE_OFF_CARE_PLAN: {
      const rawDiscount = coupon.percentageOff
        ? Math.round((coupon.percentageOff / 100) * carePlanAmount)
        : 0;

      discountAmount = Math.min(rawDiscount, carePlanAmount);
      break;
    }

    case CouponTypeEnum.PERCENTAGE_OFF_INVOICE: {
      const rawDiscount = coupon.percentageOff
        ? Math.round((coupon.percentageOff / 100) * subtotal)
        : 0;

      discountAmount = Math.min(rawDiscount, subtotal);
      break;
    }
  }

  if (coupon.isFreeShipping) {
    discountAmount += shippingAmount;
  }

  return discountAmount;
};
